import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle2, Thermometer, Clock, FileText, Shield, Users, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface LawPackUpdate {
  id: string;
  title: string;
  summary: string;
  category: string;
  severity: string;
  source_law_pack_name: string;
  published_at: string;
  selected: boolean;
}

interface LawPackUpdatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplied?: () => void;
}

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'temperature': return Thermometer;
    case 'monitoring': return Clock;
    case 'documentation': return FileText;
    case 'safety': return Shield;
    case 'compliance': return CheckCircle2;
    case 'training': return Users;
    default: return AlertCircle;
  }
};

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'critical': return 'destructive';
    case 'high': return 'default';
    case 'medium': return 'secondary';
    case 'low': return 'outline';
    default: return 'outline';
  }
};

export const LawPackUpdatesDialog: React.FC<LawPackUpdatesDialogProps> = ({
  open,
  onOpenChange,
  onApplied,
}) => {
  const { user } = useAuth();
  const [updates, setUpdates] = React.useState<LawPackUpdate[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [applying, setApplying] = React.useState(false);
  const [tenantId, setTenantId] = React.useState<string | null>(null);
  const selectedCount = updates.filter(u => u.selected).length;

  React.useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: roleRow } = await supabase
          .from('user_roles')
          .select('tenant_id')
          .eq('user_id', user.id)
          .maybeSingle();
        const tid = roleRow?.tenant_id ?? null;
        if (cancelled) return;
        setTenantId(tid);

        const { data: all, error } = await supabase
          .from('law_pack_updates')
          .select('id, title, summary, category, severity, source_law_pack_name, published_at')
          .order('published_at', { ascending: false });
        if (error) throw error;

        let handledIds = new Set<string>();
        if (tid) {
          const { data: states } = await supabase
            .from('tenant_law_pack_updates')
            .select('update_id')
            .eq('tenant_id', tid);
          handledIds = new Set((states ?? []).map(s => s.update_id));
        }
        if (cancelled) return;
        setUpdates(
          (all ?? [])
            .filter(u => !handledIds.has(u.id))
            .map(u => ({ ...u, selected: true }))
        );
      } catch (e: any) {
        toast.error(`Failed to load updates: ${e.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, user]);

  const toggleUpdate = (id: string) => {
    setUpdates(prev => prev.map(update =>
      update.id === id ? { ...update, selected: !update.selected } : update
    ));
  };

  const writeState = async (ids: string[], status: 'applied' | 'dismissed') => {
    if (!tenantId || !user || ids.length === 0) return;
    const rows = ids.map(update_id => ({
      tenant_id: tenantId,
      update_id,
      status,
      applied_by: user.id,
    }));
    const { error } = await supabase
      .from('tenant_law_pack_updates')
      .upsert(rows, { onConflict: 'tenant_id,update_id' });
    if (error) throw error;
  };

  const handleApplyUpdates = async () => {
    const ids = updates.filter(u => u.selected).map(u => u.id);
    if (ids.length === 0) return;
    setApplying(true);
    try {
      await writeState(ids, 'applied');
      toast.success(`Applied ${ids.length} update${ids.length !== 1 ? 's' : ''}`);
      onApplied?.();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(`Failed to apply updates: ${e.message}`);
    } finally {
      setApplying(false);
    }
  };

  const handleDismissAll = async () => {
    const ids = updates.map(u => u.id);
    if (ids.length === 0) { onOpenChange(false); return; }
    setApplying(true);
    try {
      await writeState(ids, 'dismissed');
      toast.success('Updates dismissed');
      onApplied?.();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(`Failed to dismiss: ${e.message}`);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-panel border-none max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl">Law Pack Updates</DialogTitle>
          <DialogDescription>
            Select the updates you want to apply to your law packs
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 py-4">
          {loading && (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading updates…
            </div>
          )}
          {!loading && updates.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
              You're all caught up — no pending updates.
            </div>
          )}
          {!loading && updates.map((update) => {
            const CategoryIcon = getCategoryIcon(update.category);
            return (
              <div
                key={update.id}
                className={`glass-panel-elevated p-4 rounded-lg border-2 transition-colors ${
                  update.selected ? 'border-primary/50' : 'border-transparent'
                }`}
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    id={update.id}
                    checked={update.selected}
                    onCheckedChange={() => toggleUpdate(update.id)}
                    className="mt-1"
                  />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <Label
                        htmlFor={update.id}
                        className="text-base font-semibold cursor-pointer leading-tight"
                      >
                        {update.title}
                      </Label>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant={getSeverityColor(update.severity) as any} className="capitalize">
                          {update.severity}
                        </Badge>
                        <CategoryIcon className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {update.summary}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Targets: <span className="font-medium">{update.source_law_pack_name}</span>
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="border-t border-border/50 pt-4">
          <div className="flex items-center justify-between w-full">
            <p className="text-sm text-muted-foreground">
              {selectedCount} of {updates.length} updates selected
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={applying}>
                Cancel
              </Button>
              {updates.length > 0 && (
                <Button variant="ghost" onClick={handleDismissAll} disabled={applying}>
                  Dismiss all
                </Button>
              )}
              <Button
                onClick={handleApplyUpdates}
                disabled={selectedCount === 0 || applying}
                className="cta-primary"
              >
                {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Apply {selectedCount} Update{selectedCount !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
